<script>
  import Button from "@components/button.svelte";
  import Dropdown from "@components/dropdown.svelte";
  import BooleanControl from "@components/boolean-control.svelte";
  import { Ban, SlidersHorizontal, Shield, Zap, Building2 } from "lucide-svelte";
  import { get, patch } from "@lib/requests";
  import { showAlert, currentClinic, currentUser, cn } from "@lib/utils";
  import { onMount } from "svelte";

  let enabled = $state(false);
  let autosend = $state(false);
  let loaded = $state(false);
  let open = $state(false);
  let savingSetting = $state(null);

  let clinic = $derived(currentClinic());
  let canManage = $derived(Boolean(currentUser()?.can_manage_invoices));

  let launcherText = $derived.by(() => {
    if (!enabled) return "Relances désactivées";
    if (autosend) return "Relances automatiques";
    return "Relances autorisées";
  });

  let launcherHint = $derived.by(() => {
    if (canManage) return `${clinic?.name || "Clinique"}: gérer les réglages de relance`;
    return "Réservé aux gestionnaires de factures";
  });

  let statusIcon = $derived(!enabled ? Ban : autosend ? Zap : Shield);
  let settingsIcon = $derived(SlidersHorizontal);
  let launcherClass = $derived(cn(
    !enabled && "outline-info",
    enabled && !autosend && "outline-primary",
    enabled && autosend && "outline-submit"
  ));
  let headerBadgeClass = $derived("shrink-0 w-9 h-9 rounded-full border flex items-center justify-center bg-slate-100 text-slate-500 border-slate-200");
  let allowedRowIconClass = $derived(cn(
    "shrink-0",
    enabled ? "text-teal-600" : "text-slate-400"
  ));
  let autosendRowIconClass = $derived(cn(
    "shrink-0",
    autosend ? "text-teal-600" : "text-slate-400"
  ));

  async function load() {
    try {
      const response = await get("/api/v1/invoice_reminders/clinic_settings");
      const data = await response.json;
      enabled = Boolean(data.enabled);
      autosend = Boolean(data.autosend);
    } catch (e) {
      // Silent — if we can't load, keep the initial (false) state.
    } finally {
      loaded = true;
    }
  }

  async function updateSetting(setting, nextValue) {
    const previousEnabled = enabled;
    const previousAutosend = autosend;

    if (setting === "enabled") {
      enabled = nextValue;
      if (!nextValue) autosend = false;
    } else {
      autosend = nextValue;
    }

    savingSetting = setting;
    try {
      const response = await patch("/api/v1/invoice_reminders/toggle_clinic", {
        body: { setting, enabled: nextValue }
      });
      const data = await response.json;
      enabled = Boolean(data.enabled);
      autosend = Boolean(data.autosend);
    } catch (e) {
      enabled = previousEnabled;
      autosend = previousAutosend;
      showAlert(e.message, { type: "error" });
    } finally {
      savingSetting = null;
    }
  }

  onMount(load);
</script>

{#if loaded}
  <Dropdown bind:open align="end" offset={12} contentClass="z-[70]" disabled={!canManage}>
    {#snippet trigger({ open })}
      <Button
        text={launcherText}
        BeforeIcon={statusIcon}
        Icon={settingsIcon}
        hint={launcherHint}
        class={launcherClass}
        disabled={!canManage}
        {open}
      />
    {/snippet}

    {#snippet contents()}
      <div class="min-w-[24rem]">
        <div class="px-4 py-3 border-b border-gray-200">
          <div class="flex items-center gap-3">
            <div class={headerBadgeClass}>
              <Building2 size={18} strokeWidth={1.8} />
            </div>
            <div>
              <p class="text-sm text-gray-700 font-medium">{clinic?.name || "Clinique"}</p>
              <p class="text-xs text-gray-500 mt-1">Gérer l’accès aux relances et l’envoi automatique.</p>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between gap-6 px-4 py-3 border-b border-gray-200">
          <div class="flex items-start gap-3">
            <div class="w-9 h-9 shrink-0 flex items-center justify-center">
              <Shield size={16} strokeWidth={1.9} class={allowedRowIconClass} />
            </div>
            <div class="flex flex-col gap-0.5">
              <span class="text-smaller text-gray-700">Relances autorisées</span>
              <span class="text-xs text-gray-500">Autoriser les relances pour cette clinique.</span>
            </div>
          </div>
          <BooleanControl
            value={enabled}
            disabled={!canManage || Boolean(savingSetting)}
            onChange={(value) => updateSetting("enabled", value)}
          />
        </div>

        <div class="flex items-center justify-between gap-6 px-4 py-3">
          <div class="flex items-start gap-3">
            <div class="w-9 h-9 shrink-0 flex items-center justify-center">
              <Zap size={16} strokeWidth={1.9} class={autosendRowIconClass} />
            </div>
            <div class="flex flex-col gap-0.5">
              <span class="text-smaller text-gray-700">Envoi automatique</span>
              <span class="text-xs text-gray-500">Activer l’envoi automatique des relances pour cette clinique.</span>
            </div>
          </div>
          <BooleanControl
            value={autosend}
            disabled={!canManage || !enabled || Boolean(savingSetting)}
            onChange={(value) => updateSetting("autosend", value)}
          />
        </div>

      </div>
    {/snippet}
  </Dropdown>
{/if}
